import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'
import type { PlotViewport } from '../contract'

/** Native owners explicitly identify their graphics; all other chrome keeps its height. */
export function usePlotGraphicBudget(
  viewport: PlotViewport | undefined,
  root: RefObject<HTMLElement>,
  graphics: ReadonlyArray<RefObject<HTMLElement | SVGElement>>,
) {
  const [budget, setBudget] = useState<{ width: number; height: number } | null>(null)
  useLayoutEffect(() => {
    if (!viewport) { setBudget(null); return }
    if (!root.current || !graphics.length) return
    let active = true
    const measure = () => {
      if (!active) return
      const element = root.current
      if (!element || graphics.some((graphic) => !graphic.current)) return
      // SVG and HTML clientHeight are layout dimensions, unaffected
      // by the deck viewer's transform. Each declared graphic is counted once.
      const graphicHeight = graphics.reduce((sum, graphic) => sum + graphic.current!.clientHeight, 0)
      const chromeHeight = element.clientHeight - graphicHeight
      const next = { width: element.clientWidth, height: (viewport.maxHeight - chromeHeight) / graphics.length }
      setBudget((previous) => previous?.width === next.width && previous.height === next.height ? previous : next)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(root.current)
    graphics.forEach((graphic) => graphic.current && observer?.observe(graphic.current))
    void document.fonts?.ready.then(measure)
    return () => { active = false; observer?.disconnect() }
  }, [viewport?.maxHeight, root, graphics])
  return viewport ? budget : null
}
